import { z } from 'zod';
import { Question, QuestionSchema } from './question';
import {
  ALLOWED_AUDIO_MIME_TYPES,
  ALLOWED_DOCUMENT_MIME_TYPES,
} from '../upload/validation';
import { SensorReadingsSchema } from './sensors';

export const TextAnswerSchema = z.string();
export const RadioAnswerSchema = z.string().nullable();
export const SelectAnswerSchema = z.array(z.string());
export const CheckboxesAnswerSchema = z.array(z.string());
export const DateAnswerSchema = z.string().date();
export const NumberAnswerSchema = z.number().nullable();
export const FILE_ANSWER_CATEGORIES = ['document', 'voice'] as const;
export type FileAnswerCategory = (typeof FILE_ANSWER_CATEGORIES)[number];

export const DocumentAnswerSchema = z.object({
  category: z.literal('document'),
  uri: z.string().min(1),
  name: z.string(),
  type: z.enum(ALLOWED_DOCUMENT_MIME_TYPES),
  size: z.number(),
  timestamp: z.string(),
});

export const VoiceAnswerSchema = z.object({
  category: z.literal('voice'),
  uri: z.string().min(1),
  name: z.string(),
  type: z.enum(ALLOWED_AUDIO_MIME_TYPES),
  timestamp: z.string(),
  size: z.number(),
  duration: z.number(),
});

export const FileAnswerSchema = z.discriminatedUnion('category', [
  DocumentAnswerSchema,
  VoiceAnswerSchema,
]);

export const AnswerSchema = z.union([
  TextAnswerSchema,
  RadioAnswerSchema,
  SelectAnswerSchema,
  CheckboxesAnswerSchema,
  DateAnswerSchema,
  NumberAnswerSchema,
  VoiceAnswerSchema,
  DocumentAnswerSchema,
]);

export type DateAnswer = z.infer<typeof DateAnswerSchema>;
export type TextAnswer = z.infer<typeof TextAnswerSchema>;
export type RadioAnswer = z.infer<typeof RadioAnswerSchema>;
export type SelectAnswer = z.infer<typeof SelectAnswerSchema>;
export type CheckboxesAnswer = z.infer<typeof CheckboxesAnswerSchema>;
export type NumberAnswer = z.infer<typeof NumberAnswerSchema>;
export type DocumentAnswer = z.infer<typeof DocumentAnswerSchema>;
export type VoiceAnswer = z.infer<typeof VoiceAnswerSchema>;

export type FileAnswer = z.infer<typeof FileAnswerSchema>;

export type Answer = z.infer<typeof AnswerSchema>;

export const QuestionAnswersSchema = z.record(z.string().uuid(), AnswerSchema);

export type QuestionAnswers = z.infer<typeof QuestionAnswersSchema>;

export const QuestionWithAnswerSchema = QuestionSchema.extend({
  answer: AnswerSchema,
});
export type QuestionWithAnswer = z.infer<typeof QuestionWithAnswerSchema>;

export const QuestionsWithAnswersSchema = z.object({
  questions: z.array(QuestionWithAnswerSchema),
});
export type QuestionsWithAnswers = z.infer<typeof QuestionsWithAnswersSchema>;

export const SubmitAnswerSchema = z
  .object({
    answers: QuestionAnswersSchema,
    startedAt: z.string().datetime(),
    submittedAt: z.string().datetime(),
    sensors: SensorReadingsSchema.optional(),
  })
  .refine(
    (data) => {
      return data.startedAt < data.submittedAt;
    },
    {
      message: 'Started at must be before submitted at',
    },
  );

export type SubmitAnswers = z.infer<typeof SubmitAnswerSchema>;

export const SubmitAnswersResponseSchema = z.object({
  studyId: z.string().uuid(),
});

export type SubmitAnswersResponse = z.infer<typeof SubmitAnswersResponseSchema>;

export const toQuestionsWithAnswers = (
  questions: Question[],
  answers: QuestionAnswers,
): QuestionWithAnswer[] => {
  return questions.map((question) => ({
    ...question,
    answer: answers[question.id] ?? null,
  }));
};

export const isDocumentAnswer = (
  answer: Answer | null | undefined,
): answer is DocumentAnswer => {
  return (
    typeof answer === 'object' &&
    answer !== null &&
    'category' in answer &&
    answer.category === 'document'
  );
};

export const isVoiceAnswer = (
  answer: Answer | null | undefined,
): answer is VoiceAnswer => {
  return (
    typeof answer === 'object' &&
    answer !== null &&
    'category' in answer &&
    answer.category === 'voice'
  );
};

export const isFileAnswer = (
  answer: Answer | null | undefined,
): answer is FileAnswer => {
  return isDocumentAnswer(answer) || isVoiceAnswer(answer);
};
